import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { HeroButton } from '@/components/ui/button-variants';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Camera, Upload } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useTranslation } from '@/contexts/TranslationContext';

interface CreatePetModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

const CreatePetModal = ({ open, onOpenChange, onSuccess }: CreatePetModalProps) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    species: '',
    breed: '',
    age: '',
    gender: '',
    description: '',
  });

  const species = [
    'Cane', 'Gatto', 'Coniglio', 'Uccello', 'Pesce', 
    'Tartaruga', 'Criceto', 'Furetto', 'Altro'
  ];

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setAvatarPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadAvatar = async (): Promise<string | null> => {
    if (!avatarFile) return null;

    // Get current session to ensure authentication
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return null;

    const fileExt = avatarFile.name.split('.').pop();
    const fileName = `${session.user.id}/${Date.now()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('pet-avatars')
      .upload(fileName, avatarFile);

    if (uploadError) {
      console.error('Error uploading avatar:', uploadError);
      return null;
    }

    const { data } = supabase.storage
      .from('pet-avatars')
      .getPublicUrl(fileName);

    return data.publicUrl;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      toast({
        title: "Errore di autenticazione",
        description: "Devi essere autenticato per aggiungere un pet.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // Verify authentication before proceeding
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        toast({
          title: "Sessione scaduta",
          description: "Effettua nuovamente l'accesso.",
          variant: "destructive",
        });
        return;
      }

      const avatarUrl = await uploadAvatar();

      const { error } = await supabase
        .from('pets')
        .insert({
          user_id: session.user.id,
          name: formData.name,
          species: formData.species,
          breed: formData.breed || null,
          age: formData.age ? parseInt(formData.age) : null,
          gender: formData.gender || null,
          description: formData.description || null,
          avatar_url: avatarUrl,
        });

      if (error) throw error;

      toast({
        title: "Pet aggiunto!",
        description: `${formData.name} è stato aggiunto al tuo profilo.`,
      });

      // Reset form
      setFormData({
        name: '',
        species: '',
        breed: '',
        age: '',
        gender: '',
        description: '',
      });
      setAvatarFile(null);
      setAvatarPreview(null);
      
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error('Error creating pet:', error);
      toast({
        title: "Errore",
        description: "Non è stato possibile aggiungere il pet.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getSpeciesEmoji = (species: string) => {
    switch (species.toLowerCase()) {
      case 'cane': return '🐕';
      case 'gatto': return '🐱';
      case 'coniglio': return '🐰';
      case 'uccello': return '🐦';
      case 'pesce': return '🐠';
      case 'tartaruga': return '🐢';
      case 'criceto': return '🐹';
      case 'furetto': return '🦫';
      default: return '🐾';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass border-white/20 max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white text-center">Aggiungi un Nuovo Pet</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Avatar Upload */}
          <div className="flex flex-col items-center space-y-4">
            <div className="relative">
              <Avatar className="w-24 h-24 ring-4 ring-primary/30">
                <AvatarImage src={avatarPreview || undefined} />
                <AvatarFallback className="bg-gradient-to-br from-primary to-secondary text-white text-2xl">
                  {formData.species ? getSpeciesEmoji(formData.species) : <Camera className="w-8 h-8" />}
                </AvatarFallback>
              </Avatar>
              <label 
                htmlFor="avatar-upload"
                className="absolute -bottom-2 -right-2 w-8 h-8 bg-primary rounded-full flex items-center justify-center cursor-pointer hover:bg-primary/80 transition-colors"
              >
                <Upload className="w-4 h-4 text-white" />
              </label>
              <input
                id="avatar-upload"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
              />
            </div>
            <p className="text-sm text-muted-foreground text-center">
              Aggiungi una foto del tuo pet
            </p>
          </div>

          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-white">Nome *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="glass border-white/20 text-white"
                placeholder="Es. Luna"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="species" className="text-white">Specie *</Label>
              <Select value={formData.species} onValueChange={(value) => setFormData({ ...formData, species: value })}>
                <SelectTrigger className="glass border-white/20 text-white">
                  <SelectValue placeholder="Seleziona" />
                </SelectTrigger>
                <SelectContent className="glass border-white/20">
                  {species.map((s) => (
                    <SelectItem key={s} value={s} className="text-white">
                      {getSpeciesEmoji(s)} {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="breed" className="text-white">Razza</Label>
              <Input
                id="breed"
                value={formData.breed}
                onChange={(e) => setFormData({ ...formData, breed: e.target.value })}
                className="glass border-white/20 text-white"
                placeholder="Es. Golden Retriever"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="age" className="text-white">Età</Label>
              <Input
                id="age"
                type="number"
                min="0"
                max="30"
                value={formData.age}
                onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                className="glass border-white/20 text-white"
                placeholder="Anni"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="gender" className="text-white">Sesso</Label>
            <Select value={formData.gender} onValueChange={(value) => setFormData({ ...formData, gender: value })}>
              <SelectTrigger className="glass border-white/20 text-white">
                <SelectValue placeholder="Seleziona" />
              </SelectTrigger>
              <SelectContent className="glass border-white/20">
                <SelectItem value="maschio" className="text-white">♂️ Maschio</SelectItem>
                <SelectItem value="femmina" className="text-white">♀️ Femmina</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description" className="text-white">Descrizione</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="glass border-white/20 text-white resize-none"
              rows={3}
              placeholder="Racconta qualcosa di speciale sul tuo pet..."
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              className="flex-1"
              disabled={loading}
            >
              Annulla
            </Button>
            <HeroButton 
              type="submit" 
              className="flex-1" 
              disabled={!formData.name || !formData.species || loading}
            >
              {loading ? "Salvataggio..." : "Aggiungi Pet"}
            </HeroButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreatePetModal;